import React from 'react';
import { SITE_CONTENT } from '../constants';

const ContactForm: React.FC = () => {
  const { location } = SITE_CONTENT;
  const [status, setStatus] = React.useState<null | 'success' | 'error'>(null);
  const [isSubmitting, setIsSubmitting] = React.useState(false);
  const [formData, setFormData] = React.useState({
    name: '',
    email: '',
    subject: '',
    message: ''
  });

  const handleChange = (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => {
    const { name, value } = e.target;
    setFormData(prev => ({ ...prev, [name]: value }));
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setIsSubmitting(true);
    setStatus(null);

    try {
      const response = await fetch('/contact.php', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify(formData),
      });

      if (response.ok) {
        setStatus('success');
        setFormData({ name: '', email: '', subject: '', message: '' });
      } else {
        setStatus('error');
      }
    } catch (error) {
      console.error('Error submitting form:', error);
      setStatus('error');
    } finally {
      setIsSubmitting(false);
      setTimeout(() => setStatus(null), 5000);
    }
  };

  return (
    <div className="max-w-7xl mx-auto px-6">
      <div className="grid grid-cols-1 lg:grid-cols-12 gap-16">
        <div className="lg:col-span-5">
          <h2 className="text-4xl md:text-5xl font-serif text-white mb-8">Contatti</h2>
          <p className="text-lg text-white/80 mb-12">
            Se desideri unirti al coro, proporre una collaborazione o ricevere informazioni sui nostri concerti, non esitare a scriverci.
          </p>
          
          <div className="space-y-8 mb-12">
            <div className="flex items-center space-x-4">
              <div className="text-gold mt-1">
                <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M17.657 16.657L13.414 20.9a1.998 1.998 0 01-2.827 0l-4.244-4.243a8 8 0 1111.314 0z" />
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M15 11a3 3 0 11-6 0 3 3 0 016 0z" />
                </svg>
              </div>
              <p className="text-white/90 whitespace-pre-line">{location.address}</p>
            </div>
            <div className="flex items-center space-x-4">
              <div className="text-gold">
                <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M3 8l7.89 5.26a2 2 0 002.22 0L21 8M5 19h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v10a2 2 0 002 2z" />
                </svg>
              </div>
              <p className="text-white/90">info@bottegadellevoci.it</p>
            </div>
          </div>

          <div className="p-1 border-2 border-gold rounded-sm bg-gold/5 shadow-[0_0_20px_rgba(212,175,55,0.4)]">
            <div className="h-64 overflow-hidden grayscale hover:grayscale-0 transition-all duration-500">
              <iframe 
                src={location.mapUrl} 
                width="100%" 
                height="100%" 
                style={{ border: 0 }} 
                allowFullScreen={false} 
                loading="lazy"
                title="Mappa della sede del coro"
              ></iframe>
            </div>
          </div>
        </div>

        <div className="lg:col-span-6 lg:col-start-7 bg-midnight p-8 md:p-12 border border-gold/10 shadow-2xl">
          <form onSubmit={handleSubmit} className="space-y-6">
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-6">
              <div>
                <label className="block text-xs uppercase tracking-widest text-gold mb-2">Nome</label>
                <input 
                  type="text" 
                  name="name"
                  value={formData.name}
                  onChange={handleChange}
                  required
                  className="w-full bg-white/5 border border-white/10 px-4 py-3 text-white gold-input transition-all"
                  placeholder="Il tuo nome"
                />
              </div>
              <div>
                <label className="block text-xs uppercase tracking-widest text-gold mb-2">Email</label>
                <input 
                  type="email" 
                  name="email"
                  value={formData.email}
                  onChange={handleChange}
                  required
                  className="w-full bg-white/5 border border-white/10 px-4 py-3 text-white gold-input transition-all"
                  placeholder="tua@email.com"
                />
              </div>
            </div>
            <div>
              <label className="block text-xs uppercase tracking-widest text-gold mb-2">Oggetto</label>
              <input 
                type="text" 
                name="subject"
                value={formData.subject}
                onChange={handleChange}
                required
                className="w-full bg-white/5 border border-white/10 px-4 py-3 text-white gold-input transition-all"
                placeholder="Qual è il motivo del contatto?"
              />
            </div>
            <div>
              <label className="block text-xs uppercase tracking-widest text-gold mb-2">Messaggio</label>
              <textarea 
                rows={5} 
                name="message"
                value={formData.message}
                onChange={handleChange}
                required
                className="w-full bg-white/5 border border-white/10 px-4 py-3 text-white gold-input transition-all resize-none"
                placeholder="Come possiamo aiutarti?"
              ></textarea>
            </div>
            
            <button 
              type="submit"
              disabled={isSubmitting}
              className="w-full bg-gold py-4 text-midnight uppercase tracking-widest hover:bg-gold-light transition-colors duration-300 shadow-xl disabled:opacity-50 disabled:cursor-not-allowed"
            >
              {isSubmitting ? 'Invio in corso...' : 'Invia Messaggio'}
            </button>

            {status === 'success' && (
              <div className="p-4 bg-green-500/20 border border-green-500 text-green-200 text-center animate-fadeIn">
                Messaggio inviato con successo! Ti ricontatteremo presto.
              </div>
            )}

            {status === 'error' && (
              <div className="p-4 bg-red-500/20 border border-red-500 text-red-200 text-center animate-fadeIn">
                Si è verificato un errore. Riprova più tardi o scrivici direttamente via email.
              </div>
            )}
          </form>
        </div>
      </div>
    </div>
  );
};

export default ContactForm;